import * as React from "react"
import * as Yup from "yup"
import { CmsVendor } from "@modules/graphql/types"

import {
  useSanityCmsProjectsForIntegrationQuery,
  useSanityCmsDatasetsForIntegrationQuery,
} from "@modules/cms/shared/queries.generated"

import {
  RadioButtonConnectedField,
  SelectConnectedField,
  Text,
} from "gatsby-interface"
import Form from "@modules/form/components/Form"
import { composeIntegrationConfig } from "../utils"
import { ConnectCmsFormProps } from "../types"
import { ConnectCmsFormControls } from "./ConnectCmsFormControls"
import { useSetCmsIntegration } from "../hooks/useSetCmsIntegration"
import { CmsDataType } from "@modules/cms/shared/utils"
import Loading from "@modules/ui/components/Loading"
import {
  ConnectCmsFormLayout,
  loadingContainerCss,
} from "./ConnectCmsFormLayout"
import { ErrorAlert } from "@modules/ui/components/ErrorAlert"
import { cmsIntegration as cmsIntegrationText } from "@modules/locales/default.js"
import {
  commonMessageTags,
  CommonTagNames,
  FormattedMessage,
} from "@modules/locales"
import { OnboardingSiteType } from "@modules/onboarding/shared/types"
import GatsbyProvisionLearnMoreMessage from "@modules/cms/shared/components/GatsbyProvisionLearnMoreMessage"
import RefreshCmsAccountDataMessage from "@modules/cms/shared/components/RefreshCmsAccountDataMessage"

type FormValues = {
  dataSourceProject: string
  dataSourceDataset: string
}

type Option = { value: string; label: string }

const validationSchema = Yup.object().shape<FormValues>({
  dataSourceProject: Yup.string().required(
    cmsIntegrationText.messages.validationSanityProjectRequired
  ),
  dataSourceDataset: Yup.string().required(
    cmsIntegrationText.messages.validationSanityDatasetRequired
  ),
})

export type ConnectSanityFormProps = ConnectCmsFormProps

export function ConnectSanityForm({
  siteId,
  siteName,
  siteType,
  onFetchError,
  onComplete,
  onCancel,
}: ConnectSanityFormProps) {
  const [manualRefresh, setManualRefresh] = React.useState(false)
  const {
    data,
    loading,
    error,
    refetch,
  } = useSanityCmsProjectsForIntegrationQuery({
    fetchPolicy: `network-only`,
    variables: {
      siteId,
    },
    notifyOnNetworkStatusChange: true,
    onError: onFetchError,
  })
  const [setCmsIntegration] = useSetCmsIntegration({
    siteId,
  })

  if (error) {
    return <ErrorAlert>{error.message}</ErrorAlert>
  }

  if (loading || data === undefined) {
    return (
      <div css={loadingContainerCss}>
        <Loading
          variant="baby"
          message={cmsIntegrationText.messages.loadingSanityProjects}
        />
      </div>
    )
  }

  const sanityProjects = data?.sanityCMSProjectsForIntegration ?? []
  const projectOptions: Option[] = sanityProjects.map(project => ({
    value: project.id,
    label: project.name,
  }))
  let initialProjectId = sanityProjects[0]?.id ?? ``
  let initialDatasetId = ``

  const initialProject = sanityProjects.find(project => project.selected)
  if (initialProject) {
    initialProjectId = initialProject.id
    initialDatasetId = initialProject.selectedDataset ?? ``
  }

  return (
    <Form<FormValues>
      initialValues={{
        dataSourceProject: initialProjectId,
        dataSourceDataset: initialDatasetId,
      }}
      validationSchema={validationSchema}
      onSubmit={values => {
        return setCmsIntegration({
          variables: {
            config: {
              ...composeIntegrationConfig(
                CmsDataType.SanityProject,
                sanityProjects,
                values.dataSourceProject
              ),
              [CmsDataType.SanityDataset]: values.dataSourceDataset,
            },
            siteId,
            vendor: CmsVendor.Sanity,
          },
        }).finally(onComplete)
      }}
      enableReinitialize
    >
      {({ isSubmitting, values, setValues }) => {
        return (
          <ConnectCmsFormLayout
            description={
              <FormattedMessage<"siteName", CommonTagNames>
                message={cmsIntegrationText.messages.chooseSanityDataSource}
                values={{ siteName }}
                tags={commonMessageTags}
              />
            }
            controls={
              <ConnectCmsFormControls
                isSubmitting={isSubmitting}
                onCancel={onCancel}
              />
            }
            refreshMessage={
              <Text size="S">
                <RefreshCmsAccountDataMessage
                  message={cmsIntegrationText.messages.refetchSanityAccountData}
                  refetch={() => {
                    setManualRefresh(true)
                    refetch()
                  }}
                />
              </Text>
            }
            instructions={
              siteType === OnboardingSiteType.DeployNowProvision ? (
                <>
                  <Text size="S">
                    <FormattedMessage<"cmsProjectName", CommonTagNames>
                      message={
                        cmsIntegrationText.messages
                          .deployNowProvisionSelectEmptyDataSource
                      }
                      values={{
                        cmsProjectName: `${CmsDataType.SanityProject} and ${CmsDataType.SanityDataset}`,
                      }}
                      tags={commonMessageTags}
                    />
                  </Text>
                  <Text size="S">
                    <GatsbyProvisionLearnMoreMessage
                      cmsVendor={CmsVendor.Sanity}
                    />
                  </Text>
                </>
              ) : null
            }
          >
            {projectOptions.length > 1 && (
              <RadioButtonConnectedField
                name="dataSourceProject"
                label={cmsIntegrationText.labels.sanityProject}
                options={projectOptions}
                variant="framed"
                onChange={e => {
                  setValues(
                    {
                      dataSourceProject: e.target.value,
                      dataSourceDataset: ``,
                    },
                    false
                  )
                }}
              />
            )}
            <SanityDatasetField
              name="dataSourceDataset"
              label={cmsIntegrationText.labels.sanityDataset}
              siteId={siteId}
              selectedProject={values.dataSourceProject}
              manualRefresh={manualRefresh}
              setManualRefresh={setManualRefresh}
            />
          </ConnectCmsFormLayout>
        )
      }}
    </Form>
  )
}

const EMPTY_DATA_SET_OPTION: Option = {
  value: ``,
  label: cmsIntegrationText.labels.sanityDatasetPlaceholder,
}

type SanityDatasetFieldProps = {
  name: string
  label: React.ReactNode
  siteId: string
  selectedProject: string
  manualRefresh?: boolean
  setManualRefresh?: (_: boolean) => void
}

function SanityDatasetField({
  name,
  label,
  siteId,
  selectedProject,
  manualRefresh = false,
  setManualRefresh,
}: SanityDatasetFieldProps) {
  const {
    data,
    loading,
    error,
    refetch,
  } = useSanityCmsDatasetsForIntegrationQuery({
    fetchPolicy: "network-only",
    variables: {
      siteId,
      projectId: selectedProject,
    },
    skip: !selectedProject,
  })

  // allow for refetch to be called from parent
  if (manualRefresh && setManualRefresh && refetch) {
    refetch()
    setManualRefresh(false)
  }

  const projectDatasets = data?.sanityCMSDatasetsForIntegration ?? []

  let options: Option[] = [EMPTY_DATA_SET_OPTION]
  if (selectedProject) {
    options = options.concat(
      projectDatasets.map(dataset => ({
        label: dataset.name,
        value: dataset.name,
      }))
    )
  }

  return (
    <React.Fragment>
      <SelectConnectedField
        name={name}
        label={label}
        options={options}
        disabled={loading}
      />
      {error && <ErrorAlert>{error.message}</ErrorAlert>}
    </React.Fragment>
  )
}
